import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Country, ReferenceValue, State } from '../_models/customer-form.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerFormService implements OnDestroy {
  private readonly API_URL = environment.apiBaseUrl;
  private readonly TOKEN_KEY = 'uuidToken';

  constructor(private http: HttpClient) {
    // Listen for window unload to clear token when session ends
    window.addEventListener('beforeunload', () => this.clearToken());
  }

  ngOnDestroy() {
    // Clear token when service is destroyed
    this.clearToken();
    window.removeEventListener('beforeunload', () => this.clearToken());
  }

  private getHeaders(isFormData: boolean = false): HttpHeaders {
    const uuidToken = localStorage.getItem(this.TOKEN_KEY);
    if (!uuidToken) {
      console.warn('No UUID token found in localStorage');
      return new HttpHeaders();
    }
    // console.log('Using UUID token for request:', uuidToken);

    const headers: { [key: string]: string } = {
      'External-Token': uuidToken,
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    return new HttpHeaders(headers);
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 401) {
      // Clear token if unauthorized
      this.clearToken();
      console.error('Authentication failed. Token cleared.');
    }
    return throwError(() => error);
  }

  private clearToken(): void {
    // console.log('Clearing UUID token from localStorage');
    localStorage.removeItem(this.TOKEN_KEY);
  }

  getCountries(): Observable<Country[]> {
    const headers = this.getHeaders();
    if (!headers.has('External-Token')) {
      console.error('No External-Token header set for request');
      return throwError(() => new Error('No authentication token available'));
    }

    // console.log('Request headers for countries:', headers.get('External-Token'));

    return this.http
      .get<any>(`${this.API_URL}/find-all-for-dropdown`, { headers })
      .pipe(
        retry(1),
        map((response) => response as Country[]),
        catchError((error) => this.handleError(error))
      );
  }

  getStates(countryCode: string): Observable<State[]> {
    const headers = this.getHeaders();
    if (!headers.has('External-Token')) {
      console.error('No External-Token header set for request');
      return throwError(() => new Error('No authentication token available'));
    }

    // console.log('Request headers for states:', headers.get('External-Token'));

    return this.http
      .get<any>(
        `${this.API_URL}/find-all-states-by-country-for-dropdown/${countryCode}`,
        { headers }
      )
      .pipe(
        retry(1),
        map((response) => {
          if (Array.isArray(response)) {
            return response.map((state) => {
              if (typeof state === 'string') {
                return {
                  id: state,
                  name: state,
                  code: state,
                };
              }
              return state;
            });
          }
          return [];
        }),
        catchError((error) => this.handleError(error))
      );
  }

  getTaxIdTypes(
    referenceTypeInternalCode: string
  ): Observable<ReferenceValue[]> {
    const headers = this.getHeaders();
    if (!headers.has('External-Token')) {
      console.error('No External-Token header set for request');
      return throwError(() => new Error('No authentication token available'));
    }

    // console.log('Request headers for tax ID types:', headers.get('External-Token'));

    return this.http
      .get<any>(
        `${this.API_URL}/find-all-referenceValues-for-dropdown-by-referenceType/${referenceTypeInternalCode}`,
        { headers }
      )
      .pipe(
        retry(1),
        map((response) => response as ReferenceValue[]),
        catchError((error) => this.handleError(error))
      );
  }

  submitCustomerForm(payload: any): Observable<any> {
    const headers = this.getHeaders(false);
    if (!headers.has('External-Token')) {
      console.error('No External-Token header set for form submission');
      return throwError(() => new Error('No authentication token available'));
    }

    // console.log('Request headers for form submission:', headers.get('External-Token'));

    return this.http.post(`${environment.apiBaseUrl}/create`, payload, { headers }).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getContract(): Observable<any> {
    return this.http.get<any>(`${environment.apiBaseUrl}/get-latest-contract-for-onBoarding`,
      { headers: this.getHeaders() }
    );
  }
}
